import React, { useState, useEffect } from "react"
import { AddressElement, useElements } from "@stripe/react-stripe-js"
import PubSub from "pubsub-js"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Input from "aldoo-ra/Input"
import Checkbox from "aldoo-ra/Checkbox"
import AldooStoreAPI from "aldoo-ra/Store/api"
import Value from "aldoo-ra/Value"

// Validate tax ID using server/Stripe API
const validateTaxIdWithStripe = async (taxId, country) => {
  if (!taxId || !country) return { valid: true, error: null }

  try {
    const response = await AldooStoreAPI.validateTaxId({ taxId, country })

    if (response.result && response.valid) {
      return { valid: true, error: null }
    }

    return {
      valid: false,
      error: response.error || 'Invalid tax ID format'
    }
  } catch (error) {
    console.error('Tax ID validation error:', error)
    return {
      valid: false,
      error: 'Unable to validate tax ID'
    }
  }
}

const InvoiceStep = ({ store, isOpen }) => {
  const texts = TextBundle({ id: "aldoo-store" })
  const elements = useElements()

  // Single Value store for all invoice form data - persists across navigation
  const [invoiceFormData, setInvoiceFormData] = Value({
    key: "checkout-invoice-form-data",
    defaultValue: {
      needsInvoice: false,
      showInvoiceForm: false,
      companyName: "",
      taxId: "",
      addressComplete: false,
      name: "",
      address: null,
      hasSavedBillingDetails: false,
      addressKey: 0 // Increment to force AddressElement remount
    }
  })

  // Ephemeral state (doesn't need to persist)
  const [showValidation, setShowValidation] = useState(false)
  const [currentCountry, setCurrentCountry] = useState(null)
  const [taxIdError, setTaxIdError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [dataLoaded, setDataLoaded] = useState(false)

  // Load billing details from API or preloaded data
  // Reload when auth changes (e.g., after magic link verification)
  useEffect(() => {
    if (!dataLoaded && isOpen && !loading) {
      setLoading(true)

      // Check preloaded data first
      const preloaded = store.preloadedInvoiceData
      if (preloaded && preloaded.useSavedBillingDetails) {
        const hasDetails = !!(preloaded.companyName || preloaded.taxId || preloaded.address?.line1)

        if (hasDetails) {
          // Fallback to user profile name if billing details don't have a name
          let userName = preloaded.name || ""
          if (!userName && (store.auth?.name || store.tempAuthForPurchase?.name)) {
            userName = store.auth?.name || store.tempAuthForPurchase?.name || ""
          }

          setInvoiceFormData({
            ...invoiceFormData,
            companyName: preloaded.companyName || "",
            taxId: preloaded.taxId || "",
            address: preloaded.address,
            name: userName,
            addressComplete: !!preloaded.address?.line1,
            hasSavedBillingDetails: true,
            needsInvoice: true,
            showInvoiceForm: true,
            addressKey: invoiceFormData.addressKey + 1
          })
        }

        setLoading(false)
        setDataLoaded(true)
        return
      }

      // Load from API if authenticated (check for auth with internal_id)
      const hasAuth = !!(store.auth?.internal_id || store.tempAuthForPurchase?.auth_token)
      if (!hasAuth) {
        setLoading(false)
        setDataLoaded(true)
        return
      }

      const loadPromise = store.tempAuthForPurchase
        ? AldooStoreAPI.loadBillingDetails(store.tempAuthForPurchase)
        : AldooStoreAPI.loadBillingDetails()

      loadPromise
        .then((data) => {
          if (data) {
            // Fallback to user profile name if billing details don't have a name
            let userName = data.name || ""
            if (!userName && (store.auth?.name || store.tempAuthForPurchase?.name)) {
              userName = store.auth?.name || store.tempAuthForPurchase?.name || ""
            }

            setInvoiceFormData({
              ...invoiceFormData,
              companyName: data.companyName || "",
              taxId: data.taxId || "",
              address: data.address,
              name: userName,
              addressComplete: !!data.address?.line1,
              hasSavedBillingDetails: true,
              needsInvoice: true,
              showInvoiceForm: true,
              addressKey: invoiceFormData.addressKey + 1
            })
          }
        })
        .catch((error) => {
          console.error("Failed to load billing details:", error)
        })
        .finally(() => {
          setLoading(false)
          setDataLoaded(true)
        })
    }
  }, [dataLoaded, isOpen, loading, store.auth, store.tempAuthForPurchase])

  // Force AddressElement remount when step opens (to apply persisted values)
  useEffect(() => {
    if (isOpen) {
      setLoading(false)
      setSubmitted(false)
      // Increment key to remount AddressElement with current data
      setInvoiceFormData({
        ...invoiceFormData,
        addressKey: invoiceFormData.addressKey + 1
      })
    }
  }, [isOpen])

  const handleSubmit = async (e) => {
    e?.preventDefault()

    if (submitted) return

    // No invoice needed
    if (!invoiceFormData.needsInvoice) {
      setSubmitted(true)
      PubSub.publish("PURCHASE_FLOW_NEXT", {
        stepData: {
          needsInvoice: false,
          useSavedBillingDetails: false
        }
      })
      return
    }

    // Validate form
    const canProceed = invoiceFormData.companyName && invoiceFormData.addressComplete
    if (!canProceed) {
      setShowValidation(true)
      return
    }

    setLoading(true)

    let address = invoiceFormData.address
    let country = currentCountry
    let name = invoiceFormData.name

    // Get fresh data from AddressElement
    if (elements) {
      const addressElement = elements.getElement('address')
      if (addressElement) {
        const { complete, value: addressValue } = await addressElement.getValue()
        if (complete && addressValue) {
          country = addressValue.address.country
          // When using split name mode, firstName and lastName are separate
          if (addressValue.firstName || addressValue.lastName) {
            name = `${addressValue.firstName || ''} ${addressValue.lastName || ''}`.trim()
          } else {
            name = addressValue.name || ''
          }
          address = {
            line1: addressValue.address.line1,
            line2: addressValue.address.line2,
            city: addressValue.address.city,
            state: addressValue.address.state,
            postal_code: addressValue.address.postal_code,
            country: addressValue.address.country,
          }
        }
      }
    }

    // Validate tax ID
    if (invoiceFormData.taxId && country) {
      const validation = await validateTaxIdWithStripe(invoiceFormData.taxId, country)
      if (!validation.valid) {
        setTaxIdError(validation.error)
        setShowValidation(true)
        setLoading(false)
        return
      }
    }

    const invoiceData = {
      needsInvoice: true,
      useSavedBillingDetails: invoiceFormData.hasSavedBillingDetails,
      companyName: invoiceFormData.companyName,
      taxId: invoiceFormData.taxId,
      address,
      addressComplete: true
    }

    // Save billing details
    try {
      await AldooStoreAPI.updateBillingDetails({
        companyName: invoiceFormData.companyName,
        taxId: invoiceFormData.taxId || null,
        address,
        name
      })

      // Update persisted data with fresh values
      setInvoiceFormData({
        ...invoiceFormData,
        address,
        name,
        addressComplete: true
      })
    } catch (error) {
      console.error('Failed to save billing details:', error)
    }

    setSubmitted(true)
    PubSub.publish("PURCHASE_FLOW_NEXT", { stepData: invoiceData })
    setLoading(false)
  }

  const handleBack = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }

    if (invoiceFormData.showInvoiceForm) {
      setInvoiceFormData({ ...invoiceFormData, showInvoiceForm: false })
      setShowValidation(false)
      setTaxIdError(null)
    } else {
      PubSub.publish("PURCHASE_FLOW_BACK")
    }
  }

  const handleTaxIdChange = (e) => {
    const newValue = e.target.value.toUpperCase()
    setInvoiceFormData({ ...invoiceFormData, taxId: newValue })
    if (taxIdError) setTaxIdError(null)
  }

  const canContinue = true

  const getMissingFields = () => {
    if (!invoiceFormData.needsInvoice) return []
    const missing = []
    if (!invoiceFormData.companyName) missing.push("Company Name")
    if (!invoiceFormData.addressComplete) missing.push("Billing Address")
    return missing
  }

  const missingFields = getMissingFields()

  // Update button state
  React.useEffect(() => {
    if (!isOpen) return

    PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", {
      stepId: 'invoice',
      canContinue: canContinue,
      loading: loading,
      onContinue: handleSubmit,
      onBack: handleBack
    })
  }, [invoiceFormData.needsInvoice, invoiceFormData.showInvoiceForm, invoiceFormData.companyName, invoiceFormData.taxId, invoiceFormData.addressComplete, invoiceFormData.hasSavedBillingDetails, loading, isOpen])

  const hasError = showValidation && (taxIdError || missingFields.length > 0)

  const getErrorMessage = () => {
    if (taxIdError) return taxIdError
    if (missingFields.length > 0) {
      if (missingFields[0] === "Company Name") {
        return texts?.companyNameRequired || "Please enter a Company Name"
      } else if (missingFields[0] === "Billing Address") {
        return texts?.billingAddressRequired || "Please enter your address"
      }
    }
    return ''
  }

  const errorMessage = getErrorMessage()

  return (
    <>
      <div className={invoiceFormData.showInvoiceForm ? 'py-8 pb-[70px]' : 'h-full flex items-center justify-center py-8'}>
        <div className={`max-w-2xl px-6 w-full ${!invoiceFormData.showInvoiceForm ? 'flex flex-col justify-center' : 'mx-auto'}`}>
        {!invoiceFormData.showInvoiceForm && (
          <div className="space-y-6 w-full max-w-md mx-auto">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="bg-primary_lt/30 rounded-full p-4">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-8 w-8 text-primary">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
              </div>
              <Typography variant="h3" className="text-text whitespace-nowrap">
                {texts?.invoiceQuestion || "Would you like an invoice?"}
              </Typography>
            </div>
            <div className="flex justify-center">
              <Checkbox
                variant="aqua_large"
                isOn={invoiceFormData.needsInvoice}
                onChange={(checked) => {
                  setInvoiceFormData({
                    ...invoiceFormData,
                    needsInvoice: checked,
                    showInvoiceForm: checked
                  })
                }}
                label={texts?.yesInvoice || "Yes, I want an invoice"}
                labelPosition="right"
              />
            </div>
          </div>
        )}

        {invoiceFormData.showInvoiceForm && invoiceFormData.needsInvoice && (
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={texts?.companyName || "Company Name"}
                value={invoiceFormData.companyName}
                onChange={(e) => {
                  setInvoiceFormData({ ...invoiceFormData, companyName: e.target.value })
                  if (showValidation) setShowValidation(false)
                }}
                placeholder={texts?.enterCompanyName || "Enter company name"}
                variant="aqua"
                required
              />

              <div>
                <Input
                  label={texts?.taxId || "Tax ID (Optional)"}
                  value={invoiceFormData.taxId}
                  onChange={handleTaxIdChange}
                  placeholder={texts?.taxIdPlaceholder || "e.g., BG123456789"}
                  variant="aqua"
                  error={!!taxIdError}
                />
                {taxIdError && (
                  <p className="text-xs text-error mt-1">{taxIdError}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-text">
                {texts?.billingAddress || "Billing Address"}
              </label>
              <AddressElement
                key={`address-${invoiceFormData.addressKey}`}
                options={{
                  mode: 'billing',
                  display: {
                    name: 'split'
                  },
                  ...(invoiceFormData.address?.line1 && {
                    defaultValues: {
                      ...(invoiceFormData.name && (() => {
                        const nameParts = invoiceFormData.name.trim().split(' ')
                        const firstName = nameParts[0] || ''
                        const lastName = nameParts.slice(1).join(' ') || ''
                        return { firstName, lastName }
                      })()),
                      address: {
                        line1: invoiceFormData.address.line1,
                        line2: invoiceFormData.address.line2 || '',
                        city: invoiceFormData.address.city,
                        state: invoiceFormData.address.state || '',
                        postal_code: invoiceFormData.address.postal_code,
                        country: invoiceFormData.address.country
                      }
                    }
                  })
                }}
                onChange={(event) => {
                  setInvoiceFormData({ ...invoiceFormData, addressComplete: event.complete })
                  if (showValidation) setShowValidation(false)
                  if (event.value?.address?.country) {
                    setCurrentCountry(event.value.address.country)
                    if (taxIdError) setTaxIdError(null)
                  }
                }}
              />
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Error Display */}
      {hasError && invoiceFormData.needsInvoice && (
        <div className="sticky bottom-0 left-0 right-0 w-full min-h-[50px] flex items-center justify-center gap-2 px-6 py-3 bg-error/10 border-t-2 border-error z-[50]" style={{ boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
          <Typography variant="body_14" className="text-error text-center my-0">
            {errorMessage}
          </Typography>
        </div>
      )}
    </>
  )
}

export default InvoiceStep
